import { Row, SeeAllRow, StatusPill } from '@/components/Rows'
import {
	RECORDS_COMING,
	VISITS_ON_PAGE,
	type VisitRecord,
	byNewest,
	byYear,
	rowStateOf,
	visitLine,
	visitSubtitle,
} from '@/features/care/visits'
import { Sheet, SheetTitle } from '@/ui/core/sheet'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { router } from 'expo-router'
import { useState } from 'react'
import { View } from 'react-native'

/** The mark at a row's right, before the chevron: what this visit has, in one object. */
const Tag = ({ visit }: { visit: VisitRecord }) =>
	rowStateOf(visit) === 'pending' ? (
		// `StatusPill` sets `self-start`, which tops it in a row. The wrapper takes that over so
		// the row's own `items-center` decides.
		<View>
			<StatusPill status="wait" label="Processing" onGround />
		</View>
	) : null

const VisitRow = ({
	visit,
	onOpen,
	line,
	last = false,
}: {
	visit: VisitRecord
	onOpen: (visit: VisitRecord) => void
	line: (visit: VisitRecord) => string
	last?: boolean
}) => (
	<Row
		// A date, not a tooth. `sparkle` is the app's general shine — the score card, the app lock,
		// the billing account all wear it — so three of them down a list of visits was decoration.
		// A calendar is the only mark that says nothing about teeth, which keeps the row about when.
		glyph="calendar"
		// The visit's own name leads. A member remembers the cleaning, not the date — the date and
		// the studio are how they tell two cleanings apart, which is a second line's job.
		title={visit.name}
		line={line(visit)}
		tag={<Tag visit={visit} />}
		// The pill rides the title and the line drops a size, so a date never breaks across two
		// lines with a pill hanging over the break.
		tagOnTitle
		lineSmall
		// A visit that produced nothing is a row and not a button: no chevron, and nothing said
		// about the absence. It carried "No results" for a while, on the argument that silence
		// reads as a visit the app lost. It does not — it reads as a visit, which is what it is,
		// and the label was the app explaining its own gaps to someone who had not asked. The
		// missing chevron is the whole message: there is nothing in here to open.
		onPress={rowStateOf(visit) === 'none' ? undefined : () => onOpen(visit)}
		last={last}
	/>
)

/**
 * The caption, and under it what is still missing.
 *
 * Not a card and not a row: two lines that read as part of the section's own label rather than as
 * a block standing in front of the visits. Every filled version of this — off-white card, outlined
 * box, a row inside the table — read as the first row of the list under it. It is temporary by
 * design, and the day the records land the promise is deleted with one `Text`.
 */
const Caption = () => (
	<View className="px-4">
		<Text size="body3" tone="soft" className="tracking-[0.08em]">
			Your visits
		</Text>
		{/* The warm grey every second line in the app is set in, which is also the caption's own
		    colour: the two are told apart by size, caps and tracking, the way a label and its
		    content are separated everywhere else. `muted` read as a disabled label. */}
		{/* Left, under a left-aligned caption: here the sentence is part of the section's own
		    label, and centring it broke that pair. On the visit page the same words stand alone
		    at the foot of the page, where centred is right. */}
		<Text size="body2" tone="soft" className="mt-1.5">
			{RECORDS_COMING}
		</Text>
	</View>
)

/**
 * The visits a member has had, newest first, in the slot Health Records used to promise.
 *
 * That section was a card with nothing in it and a "Coming Soon" pill — the page ended on an
 * apology. A member's own visits are real content for the same space, and the promise survives as
 * two lines under the caption: attached to something rather than standing in for it.
 *
 * Three visits and a See All, the trim Payments makes on charges. A row goes straight to the
 * visit's own page; the sheet holds nothing but the rest of the list, grouped by year, and a row
 * inside it goes to the same page. The sheet is a way to reach a visit, not a place to read one —
 * which is why it does not step into a detail the way the payments sheet does.
 *
 * Absent entirely with nothing to show. An empty list of visits is worse than no list: the pill on
 * the score card above already says what has not happened yet.
 */
export const VisitsSection = ({ visits }: { visits: VisitRecord[] }) => {
	const [all, setAll] = useState(false)
	if (visits.length === 0) return null

	const ordered = [...visits].sort(byNewest)
	const shown = ordered.slice(0, VISITS_ON_PAGE)
	const more = ordered.length > VISITS_ON_PAGE
	const openVisit = (visit: VisitRecord) => {
		// Closed on the way out, so coming back from the record lands on Care rather than on a
		// sheet the member has finished with.
		setAll(false)
		router.push(`/records/${visit.appointmentId}`)
	}

	return (
		<>
			<VStack gap={8}>
				<Caption />
				<View className="rounded-note bg-blush px-4">
					{shown.map((visit, index) => (
						<VisitRow
							key={visit.appointmentId}
							visit={visit}
							onOpen={openVisit}
							line={visitSubtitle}
							last={index === shown.length - 1 && !more}
						/>
					))}
					{more && <SeeAllRow onPress={() => setAll(true)} />}
				</View>
			</VStack>
			<Sheet open={all} onClose={() => setAll(false)}>
				<View className="pt-2 pb-2">
					<SheetTitle glyph="calendar">Your visits</SheetTitle>
					<VStack gap={16}>
						{byYear(ordered).map(([year, list]) => (
							<VStack key={year} gap={8}>
								<View className="min-h-[24px] flex-row items-center px-4">
									<Text size="body3" tone="soft" className="tracking-[0.08em]">
										{year}
									</Text>
								</View>
								<View className="rounded-note bg-blush px-4">
									{list.map((visit, index) => (
										<VisitRow
											key={visit.appointmentId}
											visit={visit}
											onOpen={openVisit}
											// The year is the heading's job here, so the row's
											// second line drops it.
											line={visitLine}
											last={index === list.length - 1}
										/>
									))}
								</View>
							</VStack>
						))}
					</VStack>
				</View>
			</Sheet>
		</>
	)
}
